import TagManager, { DataLayerArgs } from 'react-gtm-module'
import { sendSetAppsflyerEvent } from './postMessage'
import { DataLayerObject, GTMItems } from '../@types/dataLayerObject'
import { PaymentInfo } from '../@types/cart'
import { OrderProductWithUsable } from '../components/order/NewOrder'
import { GetOrderSheetsOrderSheetNoCouponsResponse } from '@shopby/shop-sdk'

export function formatGTMCouponName(displayCouponName: string): string {
  if (!displayCouponName) return ''
  // 괄호 앞뒤 공백 허용
  const match = displayCouponName.match(/^(.+?)\s*\((.+)\)\s*$/)
  if (match) {
    const prefix = match[1].trim()
    const details = match[2]
      .split('/')
      .map((s) => s.trim())
      .join('|')
    return `${prefix}|${details}`
  }
  return displayCouponName
}

// 쿠폰 리스트 추출 함수
function extractCouponList(couponsData?: GetOrderSheetsOrderSheetNoCouponsResponse | null) {
  return (couponsData?.products ?? [])
    .map((product) => {
      const couponNames = (product.productCoupons ?? [])
        .filter((coupon) => coupon.productCouponUsable)
        .map((coupon) => formatGTMCouponName(coupon.displayCouponName || ''))
        .filter(Boolean)
      if (couponNames.length > 0) {
        return {
          productNo: product.productNo,
          mainOption: (product.mainOption || '').replace(/\s+/g, ''),
          couponNames,
        }
      }
      return null
    })
    .filter(Boolean) as {
    productNo: number
    mainOption: string
    couponNames: string[]
  }[]
}

// GTMItems 변환 함수 (Order.tsx의 cartArray 생성 로직 참고)
export function toGTMItems(
  orderItems: OrderProductWithUsable[],
  categories: any,
  findCategory: (categories: any, categoryNo: number) => any,
  couponsData?: GetOrderSheetsOrderSheetNoCouponsResponse | null
): GTMItems[] {
  // 1. 쿠폰 정보 배열을 별도 함수로 추출
  const couponList = extractCouponList(couponsData)

  // 2. 옵션별로 쿠폰 매칭 (productNo + optionTitle 비교)
  return orderItems.flatMap((item, index: number) =>
    item.orderProductOptions.map((option) => {
      const categoryNo = item.categoryNos && item.categoryNos[0]
      const categoryInfo = categoryNo ? findCategory(categories, categoryNo)?.flatCategory[0] : null
      const optionKey = (option.optionTitle || '').replace(/\s+/g, '')
      const couponObj = couponList.find((c) => c.productNo === item.productNo && c.mainOption === optionKey)
      const couponNames = couponObj?.couponNames

      return {
        item_id: String(item.productNo),
        item_name: item.productName,
        affiliation: '어바웃피싱',
        item_list_price: option.price?.salePrice,
        item_selling_price: option.price?.salePrice - option.price?.immediateDiscountAmt,
        price: option.price?.salePrice - option.price?.immediateDiscountAmt,
        item_total_discount: option.price?.immediateDiscountAmt,
        discount: 0,
        coupon: couponNames && couponNames.length > 0 ? couponNames.join('|') : undefined,
        index,
        item_brand: item.brandName,
        quantity: option.orderCnt,
        item_category: categoryInfo?.depth1Label,
        item_category2: categoryInfo?.depth2Label,
        item_category3: categoryInfo?.depth3Label,
        item_variant: option.optionTitle,
        item_list_id: undefined, // 이 부분은 undefined 인데 보내야하는지?
        item_list_name: undefined, // 이 부분은 undefined 인데 보내야하는지?
        promotion_id: undefined, // 이 부분은 undefined 인데 보내야하는지?
        promotion_name: undefined, // 이 부분은 undefined 인데 보내야하는지?
      }
    })
  )
}

interface EventHandlerArgs {
  event: string
  ecommerce: Record<string, any>
  isAppView: boolean
  appsflyerEventName?: string
}
// 공통 트래킹 함수 (내부용)
function onEventHandler({ event, ecommerce, isAppView, appsflyerEventName }: EventHandlerArgs) {
  const viewItem: DataLayerObject = {
    dataLayer: {
      event,
      ecommerce,
    },
  }
  if (isAppView) {
    sendSetAppsflyerEvent({
      eventName: appsflyerEventName || event,
      eventType: ecommerce,
    })
  } else {
    TagManager.dataLayer(viewItem as DataLayerArgs)
  }
}

// 1. 장바구니 담기
export interface TrackAddToCartArgs {
  isAppView: boolean
  value: number
  items: GTMItems[]
  [key: string]: any
}
export function trackAddToCart({ isAppView, value, items, ...rest }: TrackAddToCartArgs) {
  onEventHandler({
    event: 'add_to_cart',
    ecommerce: { currency: 'KRW', value, items, ...rest },
    isAppView,
  })
}

// 2. 장바구니 제거
export interface TrackRemoveFromCartArgs {
  isAppView: boolean
  value: number
  items: GTMItems[]
  [key: string]: any
}
export function trackRemoveFromCart({ isAppView, value, items, ...rest }: TrackRemoveFromCartArgs) {
  onEventHandler({
    event: 'remove_from_cart',
    ecommerce: { currency: 'KRW', value, items, ...rest },
    isAppView,
  })
}

// 3. 구매 완료
export interface TrackPurchaseArgs {
  isAppView: boolean
  transaction_id: string
  value: number
  items: GTMItems[]
  coupon?: string
  shipping?: number
  tax?: number
  pay_back?: number
  reserve_discount?: number
  [key: string]: any
}
export function trackPurchase({
  isAppView,
  transaction_id,
  value,
  items,
  coupon,
  shipping,
  tax,
  pay_back,
  reserve_discount,
  ...rest
}: TrackPurchaseArgs) {
  onEventHandler({
    event: 'purchase',
    ecommerce: {
      currency: 'KRW',
      transaction_id,
      value,
      items,
      coupon,
      shipping,
      tax,
      pay_back,
      reserve_discount,
      ...rest,
    },
    isAppView,
  })
}

// 4. 환불
export interface TrackRefundArgs {
  isAppView: boolean
  transaction_id: string
  value: number
  items?: GTMItems[]
  coupon?: string
  shipping?: number
  tax?: number
  [key: string]: any
}
export function trackRefund({
  isAppView,
  transaction_id,
  value,
  items,
  coupon,
  shipping,
  tax,
  ...rest
}: TrackRefundArgs) {
  onEventHandler({
    event: 'refund',
    ecommerce: {
      currency: 'KRW',
      transaction_id,
      value,
      items,
      coupon,
      shipping,
      tax,
      ...rest,
    },
    isAppView,
  })
}

// 5. 상품 리스트 노출
export interface TrackViewItemListArgs {
  isAppView: boolean
  item_list_id?: string
  item_list_name?: string
  items: GTMItems[]
  [key: string]: any
}
export function trackViewItemList({ isAppView, item_list_id, item_list_name, items, ...rest }: TrackViewItemListArgs) {
  onEventHandler({
    event: 'view_item_list',
    ecommerce: { item_list_id, item_list_name, items, ...rest },
    isAppView,
  })
}

// 6. 상품 선택
export interface TrackSelectItemArgs {
  isAppView: boolean
  item_list_id?: string
  item_list_name?: string
  items: GTMItems[]
  [key: string]: any
}
export function trackSelectItem({ isAppView, item_list_id, item_list_name, items, ...rest }: TrackSelectItemArgs) {
  onEventHandler({
    event: 'select_item',
    ecommerce: { item_list_id, item_list_name, items, ...rest },
    isAppView,
  })
}

// 7. 프로모션 노출
export interface TrackViewPromotionArgs {
  isAppView: boolean
  creative_name?: string
  creative_slot?: string
  promotion_id?: string
  promotion_name?: string
  items: GTMItems[]
  [key: string]: any
}
export function trackViewPromotion({
  isAppView,
  creative_name,
  creative_slot,
  promotion_id,
  promotion_name,
  items,
  ...rest
}: TrackViewPromotionArgs) {
  onEventHandler({
    event: 'view_promotion',
    ecommerce: { creative_name, creative_slot, promotion_id, promotion_name, items, ...rest },
    isAppView,
  })
}

// 8. 프로모션 선택
export interface TrackSelectPromotionArgs {
  isAppView: boolean
  creative_name?: string
  creative_slot?: string
  promotion_id?: string
  promotion_name?: string
  items: GTMItems[]
  [key: string]: any
}
export function trackSelectPromotion({
  isAppView,
  creative_name,
  creative_slot,
  promotion_id,
  promotion_name,
  items,
  ...rest
}: TrackSelectPromotionArgs) {
  onEventHandler({
    event: 'select_promotion',
    ecommerce: { creative_name, creative_slot, promotion_id, promotion_name, items, ...rest },
    isAppView,
  })
}

// 9. 배송 정보 입력
export interface TrackAddShippingInfoArgs {
  isAppView: boolean
  orderPrice: PaymentInfo
  cartArray: GTMItems[]
  reserve_discount: number
}
export function trackAddShippingInfo({ isAppView, orderPrice, cartArray, reserve_discount }: TrackAddShippingInfoArgs) {
  onEventHandler({
    event: 'add_shipping_info',
    ecommerce: {
      currency: 'KRW',
      value: orderPrice?.paymentAmt,
      items: cartArray,
      pay_back: orderPrice?.accumulationAmtWhenBuyConfirm,
      reserve_discount,
    },
    isAppView,
  })
}

// 10. 결제 시작
export interface TrackBeginCheckoutArgs {
  isAppView: boolean
  orderPrice: PaymentInfo
  cartArray: GTMItems[]
  pay_back: number
  reserve_discount: number
}
export function trackBeginCheckout({
  isAppView,
  orderPrice,
  cartArray,
  pay_back,
  reserve_discount,
}: TrackBeginCheckoutArgs) {
  onEventHandler({
    event: 'begin_checkout',
    ecommerce: {
      currency: 'KRW',
      value: orderPrice?.paymentAmt,
      items: cartArray,
      pay_back,
      reserve_discount,
    },
    isAppView,
  })
}

// 11. 결제 정보 입력
export interface TrackAddPaymentInfoArgs {
  isAppView: boolean
  orderPrice: PaymentInfo
  cartArray: GTMItems[]
  selectPay: string
  reserve_discount: number
  pay_back: number
}
export function trackAddPaymentInfo({
  isAppView,
  orderPrice,
  cartArray,
  selectPay,
  pay_back,
  reserve_discount,
}: TrackAddPaymentInfoArgs) {
  onEventHandler({
    event: 'add_payment_info',
    ecommerce: {
      currency: 'KRW',
      payment_type: selectPay,
      value: orderPrice?.paymentAmt,
      items: cartArray,
      pay_back,
      reserve_discount,
    },
    isAppView,
  })
}
